import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../store';
import { displayName } from '../people';
import Icon from './Icon';
import {
  grouped,
  icon,
  line,
  meta,
  snippet,
  unreadCount,
  leadsToSignoffs,
  isHandled,
  type Notification,
} from '../notifications';

/**
 * THE BELL — a person's own call-outs, gathered from every board they are on.
 *
 * It sits at the right of the top bar with a count of what has not been seen.
 * Opening it settles that count (the approved behaviour: seen on open), while
 * the panel still shows what was new THIS open under its own heading, so nothing
 * a person came to read vanishes the instant they look at it.
 *
 * Every word shown here is drawn from src/notifications.ts, which keeps the copy
 * plain and in the reader's own terms.
 */
export default function NotificationBell() {
  const notifications = useApp((s) => s.notifications);
  const profiles = useApp((s) => s.profiles);
  const projectId = useApp((s) => s.projectId);
  const markAllNotificationsRead = useApp((s) => s.markAllNotificationsRead);
  const setProject = useApp((s) => s.setProject);
  const setView = useApp((s) => s.setView);
  const openIssue = useApp((s) => s.openIssue);

  const [open, setOpen] = useState(false);
  /* What was unread at the moment this open began — frozen so the panel can keep
     showing it under "New" for as long as it is open, even though opening also
     marks it read. Cleared when the panel closes and by "Mark all read". */
  const [newThisOpen, setNewThisOpen] = useState<Set<string>>(new Set());
  const wrapRef = useRef<HTMLDivElement>(null);

  const unread = unreadCount(notifications);
  const nameOf = (email: string) => displayName(email, profiles);

  // Open settles the count: snapshot what is new, then mark everything read.
  function toggle() {
    setOpen((was) => {
      if (was) return false;
      setNewThisOpen(new Set(notifications.filter((n) => n.readAt == null).map((n) => n.id)));
      if (unread > 0) void markAllNotificationsRead();
      return true;
    });
  }

  // Click away, or Escape, closes it — the way every other pop-down here behaves.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // For this open, "New" is what was unread when it opened; everything else is
  // "Earlier". Newest first within each, from the store's own ordering.
  const { unread: newRows, read: earlierRows } = useMemo(() => {
    const isNew = (n: Notification) => newThisOpen.has(n.id);
    const sorted = grouped(notifications); // both halves already newest-first
    const all = [...sorted.unread, ...sorted.read].sort((a, b) => b.createdAt - a.createdAt);
    return { unread: all.filter(isNew), read: all.filter((n) => !isNew(n)) };
  }, [notifications, newThisOpen]);

  function goTo(n: Notification) {
    setOpen(false);
    // Switch to the board it happened on, if that is not the one on screen. The
    // board's id and the card's id are the same ids the app already routes by.
    if (n.boardId && n.boardId !== projectId) setProject(n.boardId);
    // Route by what the call-out POINTS AT (targetKind 'issue'), not its kind, so a
    // mention, a reply, a status posted on a card and a block raised on a card all
    // open the card — the last two were falling through to the Documents list. A
    // sign-off is excluded; it leads to the Sign-offs screen below.
    if (n.targetKind === 'issue' && !leadsToSignoffs(n)) {
      setView('board');
      if (n.targetId) openIssue(n.targetId);
    } else if (leadsToSignoffs(n)) {
      // A sign-off / cover call-out leads to the Sign-offs screen, not Documents.
      setView('approvals');
    } else if (n.targetKind === 'sprint') {
      setView('sprints');
    } else if (n.targetKind === 'release') {
      setView('releases');
    } else {
      setView('documents');
    }
  }

  return (
    <div className="nb" ref={wrapRef}>
      <button
        className={`nb-btn${open ? ' is-on' : ''}`}
        onClick={toggle}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Notifications"
      >
        {/* Filled when there is something unread, an outline when caught up — the
            shape carries the state before the count is read (Vinnie, 14 Sep). */}
        <Icon name={unread > 0 ? 'bell-filled' : 'bell'} size={18} />
        {unread > 0 && <span className="nb-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className="np" role="dialog" aria-label="Notifications">
          <div className="np-head">
            <h3>Notifications</h3>
            {(newRows.length > 0 || unread > 0) && (
              <button
                className="np-mark"
                onClick={() => {
                  if (unread > 0) void markAllNotificationsRead();
                  setNewThisOpen(new Set());
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className="np-empty">
              <span className="big">
                <Icon name="bell" size={22} />
              </span>
              <p>You&rsquo;re all caught up</p>
              <p className="sub">Mentions, replies and sign-off requests will show up here.</p>
            </div>
          ) : (
            <>
              {newRows.length > 0 && (
                <>
                  <p className="np-group">New</p>
                  <ul className="np-list">
                    {newRows.map((n) => (
                      <Row key={n.id} n={n} nameOf={nameOf} isNew onOpen={() => goTo(n)} />
                    ))}
                  </ul>
                </>
              )}
              {earlierRows.length > 0 && (
                <>
                  <p className="np-group">Earlier</p>
                  <ul className="np-list">
                    {earlierRows.map((n) => (
                      <Row key={n.id} n={n} nameOf={nameOf} onOpen={() => goTo(n)} />
                    ))}
                  </ul>
                </>
              )}
              {/* No "see all activity" link here. The bell is your own list — every
                  call-out for you, across every board — and it already shows all of
                  it. Activity is a different thing (one board, everyone's events), so
                  a link to it from here promised more of the same and delivered
                  something else. The two are kept distinct on purpose. */}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Row({
  n,
  nameOf,
  isNew,
  onOpen,
}: {
  n: Notification;
  nameOf: (email: string) => string;
  isNew?: boolean;
  onOpen: () => void;
}) {
  const ic = icon(n);
  const parts = line(n, nameOf);
  const sub = snippet(n, nameOf);
  // Reflect "done" (handled in My Work), not only read (#6): a call-out dealt with
  // reads as settled here too, so the bell and the inbox never disagree about
  // what is still outstanding.
  const done = isHandled(n);
  return (
    <li className={`np-item${isNew ? ' is-unread' : ''}${done ? ' is-done' : ''}`}>
      <button className="np-hit" onClick={onOpen}>
        <span className={`np-ic ${ic.cls}`}>{ic.glyph}</span>
        <div className="np-main">
          <p className="np-line">
            {parts.map((p, i) => (p.b ? <b key={i}>{p.t}</b> : <span key={i}>{p.t}</span>))}
          </p>
          {sub && <p className="np-snip">{sub}</p>}
          <p className="np-meta">{meta(n)}</p>
        </div>
        {isNew && <span className="np-dot" aria-hidden="true" />}
        {done && (
          <span className="np-done" title="Done" aria-label="Done">
            ✓
          </span>
        )}
      </button>
    </li>
  );
}
